import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { PhoneForwarded, Search, Loader2, User } from 'lucide-react';
import { useAppUsers } from '@/hooks/useAppUsers';
import { toast } from 'sonner';

interface TransferCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTransfer: (destination: string, destinationUserId?: string) => Promise<void>;
}

export function TransferCallModal({ isOpen, onClose, onTransfer }: TransferCallModalProps) {
  const { users, loading } = useAppUsers();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [isTransferring, setIsTransferring] = useState(false);

  // Filter users with Twilio phone numbers and sort alphabetically
  const availableUsers = users
    .filter(
      (user) =>
        user.active &&
        user.twilio_phone_number &&
        (searchQuery === '' ||
          user.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.twilio_phone_number.includes(searchQuery))
    )
    .sort((a, b) => {
      const nameA = (a.display_name || a.email || '').toLowerCase();
      const nameB = (b.display_name || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

  const handleTransfer = async () => {
    try {
      const user = users.find((u) => u.id === selectedUser);
      if (!user || !user.twilio_phone_number) {
        toast.error('Please select a valid user');
        return;
      }

      // Para usuários internos WebRTC, usar client:auth_user_id
      const destination = `client:${user.auth_user_id}`;
      
      setIsTransferring(true);
      await onTransfer(destination, user.id);
      
      if (window.innerWidth >= 768) {
        toast.info('Transferring...');
      }
      resetAndClose();
    } catch (error) {
      console.error('Error transferring call:', error);
      toast.error('Error transferring call');
    } finally {
      setIsTransferring(false);
    }
  };

  const resetAndClose = () => {
    setSearchQuery('');
    setSelectedUser(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PhoneForwarded className="w-5 h-5 text-primary" />
            Transfer Call
          </DialogTitle>
          <DialogDescription>
            Select the internal user to transfer the call to
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search user..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Users List */}
          <ScrollArea className="h-[300px] border rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-4">
                <User className="w-8 h-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? 'No users found'
                    : 'No users available with Twilio number'}
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-1">
                {availableUsers.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUser(user.id)}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                      selectedUser === user.id
                        ? 'bg-primary/10 border-2 border-primary'
                        : 'hover:bg-muted border-2 border-transparent'
                    }`}
                  >
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-primary/20 text-primary font-semibold">
                        {user.display_name
                          ?.split(' ')
                          .map((n) => n[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2) || '??'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 text-left">
                      <p className="font-medium text-sm">{user.display_name || 'Unnamed'}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={resetAndClose} disabled={isTransferring}>
            Cancel
          </Button>
          <Button
            onClick={handleTransfer}
            disabled={isTransferring || !selectedUser}
            className="gap-2"
          >
            {isTransferring ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Transferring...
              </>
            ) : (
              <>
                <PhoneForwarded className="w-4 h-4" />
                Transfer
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
